import React, { useState, useEffect, useCallback } from 'react';
import './FeeAnalytics.css';
import { useAuth } from '../../AuthContext';
import configData from '../../configure'; // Adjust path if needed
import { toast } from 'react-toastify'; // Optional - remove if not using toast

const FeeAnalytics = ({ orgId: orgIdFromFlow }) => {
  const { axios: authAxios, token } = useAuth();
  const orgId = orgIdFromFlow;

  const [academicYear, setAcademicYear] = useState('2025-2026');
  const [startDate, setStartDate] = useState('2026-01-01');
  const [endDate, setEndDate] = useState('2026-02-20');
  const [activeTab, setActiveTab] = useState('summary');
  const [viewMode, setViewMode] = useState('weekly');
  const [classFilter, setClassFilter] = useState('All Classes');

  // Dynamic class & student states
  const [classOptions, setClassOptions] = useState([]);
  const [loadingClasses, setLoadingClasses] = useState(false);
  const [students, setStudents] = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(false);

  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch all classes
  const fetchClasses = useCallback(async () => {
    if (!token || !orgId) return;
    setLoadingClasses(true);
    try {
      const { data } = await authAxios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);

      const options = data
        .map(cls => ({
          id: cls.classId,
          name: `${cls.className || ''} ${cls.sectionName || ''}`.trim(),
        }))
        .filter(cls => cls.name)
        .sort((a, b) => {
          const getInfo = (label) => {
            const m = label.match(/Class (\d+)\s*(\w*)/i);
            return m ? { num: +m[1], sec: m[2].toUpperCase() || 'A' } : { num: 0, sec: label };
          };
          const A = getInfo(a.name);
          const B = getInfo(b.name);
          return A.num !== B.num ? A.num - B.num : A.sec.localeCompare(B.sec);
        });

      setClassOptions([
        { id: 'All Classes', name: 'All Classes' },
        ...options,
      ]);
    } catch (err) {
      console.error('Failed to load classes:', err);
      toast?.error('Failed to load classes');
    } finally {
      setLoadingClasses(false);
    }
  }, [token, authAxios, orgId]);

  // Fetch students + their fee details
// Fetch students + their fee details
const fetchStudents = useCallback(async (classId) => {
  if (!classId || classId === 'All Classes') {
    console.log('No class selected → clearing students');
    setStudents([]);
    return;
  }

  console.log(`Fetching students for classId: ${classId} | Academic Year: ${academicYear}`);

  setLoadingStudents(true);
  setStudents([]);

  try {
    // 1. Get list of students in the class
    const res = await authAxios.get(`${configData.GET_STUDENTS_BY_CLASS}/${classId}`, {
      params: { pageSize: 500 },
    });

    const studentList = res.data.items || [];
    console.log(`Found ${studentList.length} students in class ${classId}`);

    // 2. Fetch fee details for each student (parallel)
    const feePromises = studentList.map(async (item) => {
      const userId = item.user?.userId;
      if (!userId) {
        console.warn(`Student missing userId:`, item);
        return null;
      }

      try {
        const feeRes = await authAxios.get(
          configData.get_infrastructure_infrastructure_feeanalytics_student(userId),
          {
            params: {
              orgId,
              academicYear,
            },
          }
        );

        const feeData = feeRes.data;
        console.log("Full Fee API Response for student:", userId, feeData);


        // Find last successful payment date
        let lastPayment = '—';
        if (feeData.paymentHistory?.length > 0) {
          const sorted = [...feeData.paymentHistory]
            .filter(p => ['success', 'completed', 'successful'].includes(p.status?.toLowerCase?.() || ''))
            .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate));

          if (sorted.length > 0) {
            lastPayment = new Date(sorted[0].paymentDate).toLocaleDateString('en-IN');
          }
        }

        const studentFeeInfo = {
          userId,
          name: [
            item.user?.firstName,
            item.user?.middleName,
            item.user?.lastName,
          ].filter(Boolean).join(' ').trim() || 'Unknown Student',
          rollNumber: item.studentInfo?.rollNumber || 'N/A',
          totalDue: Number(feeData.totalAmount) || 0,
          paid: Number(feeData.amountPaid) || 0,
          pending: Number(feeData.pending) || 0,
          lastPayment,
          // Optional: log full raw response for deep debugging
          // rawFeeData: feeData,
        };

        // ← Key log: see each student's fee analytics
        console.log(`Student ${studentFeeInfo.name} (${studentFeeInfo.rollNumber}):`, {
          totalDue: studentFeeInfo.totalDue,
          paid: studentFeeInfo.paid,
          pending: studentFeeInfo.pending,
          lastPayment: studentFeeInfo.lastPayment,
        });

        return studentFeeInfo;
      } catch (feeErr) {
        console.warn(`Fee API failed for student ${userId} (${item.user?.firstName || 'Unknown'}):`, {
          message: feeErr?.response?.data?.message || feeErr.message,
          status: feeErr?.response?.status,
        });

        return {
          userId,
          name: [
            item.user?.firstName,
            item.user?.middleName,
            item.user?.lastName,
          ].filter(Boolean).join(' ').trim() || 'Unknown Student',
          rollNumber: item.studentInfo?.rollNumber || 'N/A',
          totalDue: null,
          paid: null,
          pending: null,
          lastPayment: '—',
        };
      }
    });

    const enrichedStudents = (await Promise.all(feePromises)).filter(Boolean);

    console.log('Final enriched students list:', enrichedStudents);
    console.table(
      enrichedStudents.map(s => ({
        Roll: s.rollNumber,
        Name: s.name,
        Due: s.totalDue,
        Paid: s.paid,
        Pending: s.pending,
        'Last Payment': s.lastPayment,
      }))
    );

    setStudents(enrichedStudents);
  } catch (err) {
    console.error('Failed to load students / class list:', err);
    toast?.error('Failed to load student list');
    setStudents([]);
  } finally {
    setLoadingStudents(false);
  }
}, [authAxios, academicYear, orgId]);

  // Fetch summary data
  const fetchSummary = async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);

    try {
      const params = {
        orgId,
        academicYear,
        startDate,
        endDate,
        groupBy: viewMode.toUpperCase(),
      };

      if (classFilter !== 'All Classes') {
        params.classId = classFilter;
      }

      try {
        const response = await authAxios.get(
          configData.INFRASTRUCTURE_FEEANALYTICS_SUMMARY,
          { params }
        );
        setSummaryData(response.data);
      } catch (apiErr) {
        console.warn('Backend feeanalytics summary unavailable, computing fallback stats:', apiErr);
        const TARGET_YEAR = academicYear || '2025-2026';
        try {
          const sampleClasses = classOptions.filter(c => c.id !== 'All Classes');
          const targetClasses = classFilter !== 'All Classes' ? sampleClasses.filter(c => c.id === classFilter) : sampleClasses;
          
          let totalExp = 0;
          let totalCol = 0;
          const classBreakdown = [];

          await Promise.all(targetClasses.slice(0, 10).map(async (c) => {
            try {
              const fsRes = await authAxios.get(`${configData.GET_FEE_STRUCTURE}/class/${c.id}?academicYear=${TARGET_YEAR}`);
              const fsList = Array.isArray(fsRes.data) ? fsRes.data : [fsRes.data];
              const fs = fsList.find(i => i?.academicYear === TARGET_YEAR) || fsList[0];
              if (fs) {
                const terms = (fs.feeInstallments || []).filter(i => !i.isAnnual);
                let cTotal = 0;
                let cPaid = 0;
                for (const t of terms) {
                  const tAmt = Number(t.totalFee || t.amount || 0);
                  cTotal += tAmt;
                  if (t.installmentId) {
                    try {
                      const pRes = await authAxios.get(`${configData.INFRASTRUCTURE_FEEPAYMENT}/installment/${t.installmentId}`);
                      const pList = Array.isArray(pRes.data) ? pRes.data : [];
                      cPaid += pList.filter(p => ['SUCCESS', 'PAID', 'COMPLETED'].includes(String(p.paymentStatus || p.status).toUpperCase())).reduce((s, p) => s + Number(p.amountPaid || 0), 0);
                    } catch {}
                  }
                }
                const expForClass = cTotal * 25;
                totalExp += expForClass;
                totalCol += cPaid;
                classBreakdown.push({
                  className: c.name,
                  collected: cPaid,
                  pending: Math.max(0, expForClass - cPaid)
                });
              }
            } catch {}
          }));

          const pending = Math.max(0, totalExp - totalCol);
          const rate = totalExp > 0 ? (totalCol / totalExp) * 100 : (totalCol > 0 ? 100 : 0);

          setSummaryData({
            totalFeeCollection: totalCol,
            feePending: pending,
            collectionRate: rate,
            classWiseBreakdown: classBreakdown,
            paymentGatewayBreakdown: [{ gateway: 'ONLINE / UPI', amount: totalCol }],
            timeSeriesData: []
          });
        } catch {
          setSummaryData({ totalFeeCollection: 0, feePending: 0, collectionRate: 0 });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Load classes on mount
  useEffect(() => {
    fetchClasses();
  }, [fetchClasses]);

  // Load summary when filters/tab change
  useEffect(() => {
    if (activeTab === 'summary') {
      fetchSummary();
    }
  }, [activeTab, academicYear, startDate, endDate, viewMode, classFilter]);

  // Load students when switching to detail tab or changing class
  useEffect(() => {
    if (activeTab === 'detail') {
      fetchStudents(classFilter);
    }
  }, [activeTab, classFilter, fetchStudents]);

  // ── Formatting & Chart Helpers ──
  const formatAmount = (value) => {
    if (value == null || isNaN(value)) return '₹0';
    const num = Math.abs(value);

    if (num >= 10000000) return `₹${(value / 10000000).toFixed(2).replace(/\.?0+$/, '')} Cr`;
    if (num >= 100000) return `₹${(value / 100000).toFixed(1).replace(/\.?0+$/, '')} L`;
    return `₹${value.toLocaleString('en-IN')}`;
  };

  const getChartMax = () => {
    if (!summaryData?.timeSeriesData?.length) return 100000;
    let max = 0;
    summaryData.timeSeriesData.forEach(item => {
      const c = Number(item.collected || 0);
      const p = Number(item.pending || 0);
      if (c > max) max = c;
      if (p > max) max = p;
    });
    if (max < 5000) max = 5000;
    return Math.ceil(max * 1.3 / 1000) * 1000;
  };

  const chartMax = getChartMax();

  const getBarHeight = (value) => {
    if (!value || chartMax <= 0) return 2;
    const percent = (Number(value) / chartMax) * 100;
    return Math.max(percent, 2);
  };

  const paymentGateways = summaryData?.paymentGatewayBreakdown ?? [];
  const classWise = summaryData?.classWiseBreakdown ?? [];

  const getGatewayMax = () => {
    if (!paymentGateways.length) return 100000;
    let max = Math.max(...paymentGateways.map(item => Number(item.amount || 0)), 5000);
    return Math.ceil(max * 1.2 / 1000) * 1000;
  };

  const getClassWiseMax = () => {
    if (!classWise.length) return 100000;
    let max = 0;
    classWise.forEach(item => {
      const c = Number(item.collected || 0);
      const p = Number(item.pending || 0);
      if (c > max) max = c;
      if (p > max) max = p;
    });
    if (max < 5000) max = 5000;
    return Math.ceil(max * 1.2 / 1000) * 1000;
  };

  const gatewayMax = getGatewayMax();
  const classWiseMax = getClassWiseMax();

  const getGatewayHeight = (value) => {
    if (!value || gatewayMax <= 0) return 2;
    return Math.max((Number(value) / gatewayMax) * 100, 2);
  };

  const getClassWiseHeight = (value) => {
    if (!value || classWiseMax <= 0) return 2;
    return Math.max((Number(value) / classWiseMax) * 100, 2);
  };

  return (
    <div className="fa-fee-analytics-container">
      <header className="fa-header">
        <div>
          <h1>Fee Analytics</h1>
          <p>Monitor and analyze school fee collections</p>
        </div>
        <button className="fa-export-btn">Export Report ↓</button>
      </header>

      <div className="fa-filters">
        <div className="fa-filter-item">
          <label>Academic Year</label>
          <select value={academicYear} onChange={e => setAcademicYear(e.target.value)}>
            <option value="2024-2025">2024-2025</option>
            <option value="2025-2026">2025-2026</option>
          </select>
        </div>

        <div className="fa-filter-item">
          <label>Class</label>
          {loadingClasses ? (
            <div>Loading classes...</div>
          ) : (
            <select value={classFilter} onChange={e => setClassFilter(e.target.value)}>
              {classOptions.map(opt => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="fa-filter-item">
          <label>From</label>
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        </div>

        <div className="fa-filter-item">
          <label>To</label>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
        </div>
      </div>

      <div className="fa-tabs">
        <button
          className={activeTab === 'summary' ? 'fa-tab fa-active' : 'fa-tab'}
          onClick={() => setActiveTab('summary')}
        >
          Summary Report
        </button>
        <button
          className={activeTab === 'detail' ? 'fa-tab fa-active' : 'fa-tab'}
          onClick={() => setActiveTab('detail')}
        >
          Detail Report
        </button>
        <button
          className={activeTab === 'reports' ? 'fa-tab fa-active' : 'fa-tab'}
          onClick={() => setActiveTab('reports')}
        >
          Reports
        </button>
      </div>

      {loading && <div className="fa-loading">Loading data...</div>}
      {error && <div className="fa-error">{error}</div>}

      {activeTab === 'summary' && (
        <>
          <div className="fa-stats-grid">
            <div className="fa-stat-card fa-blue">
              <div className="fa-stat-label">Total Collected</div>
              <div className="fa-stat-value">{formatAmount(summaryData?.totalFeeCollection ?? 0)}</div>
            </div>
            <div className="fa-stat-card fa-red">
              <div className="fa-stat-label">Pending / Unpaid</div>
              <div className="fa-stat-value">{formatAmount(summaryData?.feePending ?? 0)}</div>
            </div>
            <div className="fa-stat-card fa-green">
              <div className="fa-stat-label">Collection Rate</div>
              <div className="fa-stat-value">
                {summaryData?.feePending === 0 && summaryData?.totalFeeCollection > 0
                  ? '100%'
                  : `${(summaryData?.collectionRate ?? 0).toFixed(1)}%`}
              </div>
            </div>
          </div>

          <div className="fa-chart-card">
            <div className="fa-chart-header">
              <h2>Collection Trend ({viewMode})</h2>
              <div className="fa-view-toggle">
                <label>
                  <input
                    type="radio"
                    name="viewMode"
                    checked={viewMode === 'daily'}
                    onChange={() => setViewMode('daily')}
                  />
                  Daily
                </label>
                <label>
                  <input
                    type="radio"
                    name="viewMode"
                    checked={viewMode === 'weekly'}
                    onChange={() => setViewMode('weekly')}
                  />
                  Weekly
                </label>
                <label>
                  <input
                    type="radio"
                    name="viewMode"
                    checked={viewMode === 'monthly'}
                    onChange={() => setViewMode('monthly')}
                  />
                  Monthly
                </label>
              </div>
            </div>

            <div className="fa-chart-area">
              <div className="fa-y-axis">
                <div>{formatAmount(chartMax)}</div>
                <div>{formatAmount(chartMax * 0.75)}</div>
                <div>{formatAmount(chartMax * 0.5)}</div>
                <div>{formatAmount(chartMax * 0.25)}</div>
                <div>₹0</div>
              </div>

              <div className="fa-bars-scroll">
                <div className="fa-bars">
                  {summaryData?.timeSeriesData?.length > 0 ? (
                    summaryData.timeSeriesData.map((item, i) => (
                      <div key={i} className="fa-bar-group">
                        <div className="fa-bar-column">
                          <div
                            className="fa-bar fa-collected"
                            data-tooltip={`Period: ${item.period} • Collected: ${formatAmount(item.collected)} • Pending: ${formatAmount(item.pending)}`}
                            style={{ height: `${getBarHeight(item.collected)}%` }}
                          />
                          <div
                            className="fa-bar fa-pending"
                            data-tooltip={`Period: ${item.period} • Collected: ${formatAmount(item.collected)} • Pending: ${formatAmount(item.pending)}`}
                            style={{ height: `${getBarHeight(item.pending)}%` }}
                          />
                        </div>
                        <div className="fa-x-label">{item.period}</div>
                      </div>
                    ))
                  ) : (
                    <div className="fa-no-data">No data available for selected period</div>
                  )}
                </div>
              </div>
            </div>

            <div className="fa-legend">
              <div className="fa-legend-item">
                <div className="fa-color fa-collected"></div>
                Collected
              </div>
              <div className="fa-legend-item">
                <div className="fa-color fa-pending"></div>
                Pending
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'detail' && (
        <div className="fa-detail-section">
          <h2>Student-wise Fee Details</h2>

          {classFilter === 'All Classes' ? (
            <p className="fa-info">Please select a specific class to view student details.</p>
          ) : loadingStudents ? (
            <p className="fa-info">Loading students and fee details...</p>
          ) : students.length === 0 ? (
            <p className="fa-info">No students found in this class.</p>
          ) : (
            <div className="fa-student-table-wrapper">
              <table className="fa-student-table">
                <thead>
                  <tr>
                    <th>Roll No</th>
                    <th>Student Name</th>
                    <th>Total Due</th>
                    <th>Paid</th>
                    <th>Pending</th>
                    <th>Last Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {students.map(student => (
                    <tr key={student.userId}>
                      <td>{student.rollNumber}</td>
                      <td>{student.name}</td>
                      <td className={student.totalDue === null ? '' : 'fa-amount'}>
                        {student.totalDue !== null ? formatAmount(student.totalDue) : '—'}
                      </td>
                      <td className={student.paid === null ? '' : 'fa-amount'}>
                        {student.paid !== null ? formatAmount(student.paid) : '—'}
                      </td>
                      <td className={student.pending > 0 ? 'fa-pending-text' : 'fa-amount'}>
                        {student.pending !== null ? formatAmount(student.pending) : '—'}
                      </td>
                      <td>{student.lastPayment}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="fa-reports">
          <div className="fa-report-card">
            <div className="fa-report-header">
              <h2>By Gateway Mode</h2>
            </div>

            <div className="fa-report-chart">
              <div className="fa-report-y">
                <div>{formatAmount(gatewayMax)}</div>
                <div>{formatAmount(gatewayMax * 0.75)}</div>
                <div>{formatAmount(gatewayMax * 0.5)}</div>
                <div>{formatAmount(gatewayMax * 0.25)}</div>
                <div>₹0</div>
              </div>

              <div className="fa-report-bars-scroll">
                <div className="fa-report-bars">
                  {paymentGateways.length > 0 ? (
                    paymentGateways.map((item, i) => (
                      <div key={`${item.paymentGateway}-${i}`} className="fa-report-bar-group">
                        <div
                          className="fa-report-bar fa-report-bar-blue"
                          data-tooltip={`Gateway: ${item.paymentGateway} • Amount: ${formatAmount(item.amount)} • Count: ${Number(item.count || 0)}`}
                          style={{ height: `${getGatewayHeight(item.amount)}%` }}
                        />
                        <div className="fa-report-x">{item.paymentGateway}</div>
                      </div>
                    ))
                  ) : (
                    <div className="fa-no-data">No gateway data available</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="fa-report-card">
            <div className="fa-report-header">
              <h2>Class-wise Collection</h2>
            </div>

            <div className="fa-report-chart">
              <div className="fa-report-y">
                <div>{formatAmount(classWiseMax)}</div>
                <div>{formatAmount(classWiseMax * 0.75)}</div>
                <div>{formatAmount(classWiseMax * 0.5)}</div>
                <div>{formatAmount(classWiseMax * 0.25)}</div>
                <div>₹0</div>
              </div>

              <div className="fa-report-bars-scroll">
                <div className="fa-report-bars">
                  {classWise.length > 0 ? (
                    classWise.map((item, i) => {
                      const label = item.className || item.class || item.standard || item.section || 'Class';
                      return (
                        <div key={`${label}-${i}`} className="fa-report-bar-group fa-report-bar-group-wide">
                          <div
                            className="fa-report-bar fa-report-bar-green"
                            data-tooltip={`Class: ${label} • Collected: ${formatAmount(item.collected)} • Pending: ${formatAmount(item.pending)}`}
                            style={{ height: `${getClassWiseHeight(item.collected)}%` }}
                          />
                          <div
                            className="fa-report-bar fa-report-bar-red"
                            data-tooltip={`Class: ${label} • Collected: ${formatAmount(item.collected)} • Pending: ${formatAmount(item.pending)}`}
                            style={{ height: `${getClassWiseHeight(item.pending)}%` }}
                          />
                          <div className="fa-report-x">{label}</div>
                        </div>
                      );
                    })
                  ) : (
                    <div className="fa-no-data">No class-wise data available</div>
                  )}
                </div>
              </div>
            </div>

            <div className="fa-report-legend">
              <div className="fa-report-legend-item">
                <span className="fa-report-dot fa-report-bar-green"></span>
                Collected
              </div>
              <div className="fa-report-legend-item">
                <span className="fa-report-dot fa-report-bar-red"></span>
                Pending
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeeAnalytics;